import { useOrganizations } from '@contexts/organizations-provider';
import * as React from 'react';

import type {
  UsersDataContextValue,
  UsersDataProviderProps,
  UsersQueryState,
  SortDirection,
} from './UsersDataContext.types';

import { useUsers } from '../../hooks/use-users/useUsers';
import { useUsersFilter } from '../filter/useUsersFilter';
import {
  INITIAL_DATA_STATE,
  INITIAL_USERS_RESPONSE,
} from './UsersDataProvider.utils';

const UsersDataContext = React.createContext<UsersDataContextValue | undefined>(
  undefined
);

const UsersDataProvider = React.memo(
  ({ children }: Readonly<UsersDataProviderProps>): React.ReactElement => {
    const { selectedOrganization } = useOrganizations();
    const { filters } = useUsersFilter();

    const [pageSize, setPageSize] = React.useState<number>(
      INITIAL_DATA_STATE.pageSize
    );
    const [pageIndex, setPageIndex] = React.useState<number>(
      INITIAL_DATA_STATE.pageIndex
    );
    const [sortBy, setSortBy] = React.useState<string>(
      INITIAL_DATA_STATE.sortBy
    );
    const [sortDirection, setSortDirection] = React.useState<SortDirection>(
      INITIAL_DATA_STATE.sortDirection
    );

    // Reset page index when filters change
    const filterKey = `${filters.searchText}|${filters.roles.join(',')}|${filters.status.join(',')}`;
    const prevFilterKey = React.useRef(filterKey);

    React.useEffect(() => {
      if (prevFilterKey.current !== filterKey) {
        setPageIndex(0);
        prevFilterKey.current = filterKey;
      }
    }, [filterKey]);

    const usersQuery = useUsers({
      organizationId: selectedOrganization?.id ?? '',
      pageSize,
      pageIndex,
      sortBy: sortBy || undefined,
      sortDirection: sortDirection === false ? undefined : sortDirection,
      searchText: filters.searchText || undefined,
      status: filters.status.length > 0 ? filters.status : undefined,
      roles: filters.roles.length > 0 ? filters.roles : undefined,
    });

    const users: UsersQueryState = React.useMemo(
      () => ({
        data: usersQuery.data ?? INITIAL_USERS_RESPONSE,
        isLoading: usersQuery.isLoading,
        isError: usersQuery.isError,
      }),
      [usersQuery.data, usersQuery.isLoading, usersQuery.isError]
    );

    const handlePageIndexChange = React.useCallback(
      (newPageIndex: number): void => {
        setPageIndex(newPageIndex);
      },
      []
    );

    const handlePageSizeChange = React.useCallback(
      (newPageSize: number): void => {
        setPageSize(newPageSize);
        setPageIndex(0);
      },
      []
    );

    const handleSortChange = React.useCallback(
      (property: string, direction: SortDirection): void => {
        if (direction === false) {
          setSortBy('');
          setSortDirection('asc');
        } else {
          setSortBy(property);
          setSortDirection(direction);
        }
        setPageIndex(0);
      },
      []
    );

    const value: UsersDataContextValue = React.useMemo(
      () => ({
        users,
        pageSize,
        pageIndex,
        sortBy,
        sortDirection,
        handlePageIndexChange,
        handlePageSizeChange,
        handleSortChange,
      }),
      [
        users,
        pageSize,
        pageIndex,
        sortBy,
        sortDirection,
        handlePageIndexChange,
        handlePageSizeChange,
        handleSortChange,
      ]
    );

    return (
      <UsersDataContext value={value}>
        {children}
      </UsersDataContext>
    );
  }
);

UsersDataProvider.displayName = 'UsersDataProvider';

export { UsersDataContext, UsersDataProvider };
